/**
 * Input classification: what a typed string means.
 *
 * `classifyInput` is the single decision point behind Requirements 2.5, 2.6, and
 * 2.7. The cell editor (task 12) calls it on commit and stores whatever it
 * returns, so the three rules of the requirement live in one pure function:
 *
 * 1. Content starting with `=` is a formula (2.5).
 * 2. Content matching a number, date, time, percentage, or currency literal is
 *    that typed value, together with the number format that displays it (2.6).
 * 3. Anything else is text (2.7).
 *
 * The number format returned for a literal is chosen so that formatting the value
 * reproduces the typed text wherever the text was already in canonical form:
 * `12.5%` keeps one decimal place, `$1,234.50` keeps two places and grouping,
 * `2024-03-05` keeps the ISO layout. That is what makes the "displayed text
 * re-classifies to the same value" half of Property 7 hold.
 *
 * Deliberate non-literals: `TRUE`, `FALSE`, and error codes such as `#REF!` are
 * text here. Requirement 2.6 lists exactly five literal kinds, and Requirement 2.7
 * sends everything else to text; the evaluator still produces booleans and errors
 * from formulas, which is where they come from in practice.
 */

import type { CellValue } from '../types.js';
import {
  MAX_DECIMALS,
  numberFormat,
  type DateTimePattern,
  type NumberFormat,
} from './numberFormat.js';
import {
  isValidHms,
  isValidYmd,
  serialFromDate,
  serialFromDateTime,
  fractionFromTime,
} from './serial.js';

/** Currency symbols recognised as a prefix on a numeric literal. */
export const CURRENCY_SYMBOLS: readonly string[] = ['$', '€', '£', '¥', '₹', 'CHF', 'kr'];

/** What a typed string classifies as. */
export type ClassifiedInput =
  /** Empty input: the cell is cleared. */
  | { k: 'blank'; value: CellValue }
  /** Content beginning with `=` (Requirement 2.5). The source keeps the `=`. */
  | { k: 'formula'; source: string }
  /** A recognised typed literal plus its number format (Requirement 2.6). */
  | { k: 'literal'; value: CellValue; format: NumberFormat }
  /** Anything else (Requirement 2.7). */
  | { k: 'text'; value: CellValue };

/** The numeric part of a literal, plus the layout choices it implies. */
interface NumericParse {
  value: number;
  /** Decimal places typed, used as the format's decimal-place count. */
  decimals: number;
  /** True when the typed text used thousands separators. */
  grouping: boolean;
}

const NUMERIC_RE =
  /^([+-]?)((?:\d{1,3}(?:,\d{3})+)|\d+)?(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/;

/**
 * Parses a bare numeric literal, accepting an optional sign, optional thousands
 * separators in strict groups of three, an optional fraction, and an optional
 * exponent. Returns `null` when the text is not a number at all.
 */
function parseNumeric(text: string): NumericParse | null {
  const m = NUMERIC_RE.exec(text);
  if (m === null) return null;
  const sign = m[1] ?? '';
  const intPart = m[2];
  const frac = m[3];
  const exp = m[4];
  if (intPart === undefined && frac === undefined) return null;
  const digits = (intPart ?? '0').replace(/,/g, '');
  const literal = `${sign}${digits}${frac === undefined ? '' : `.${frac}`}${
    exp === undefined ? '' : `e${exp}`
  }`;
  const value = Number(literal);
  if (!Number.isFinite(value)) return null;
  return {
    value,
    decimals: Math.min(frac?.length ?? 0, MAX_DECIMALS),
    grouping: intPart !== undefined && intPart.includes(','),
  };
}

/** `50%` and `12.5%`: the value is scaled, the decimal places are kept. */
function parsePercentage(text: string): ClassifiedInput | null {
  if (!text.endsWith('%')) return null;
  const numeric = parseNumeric(text.slice(0, -1).trimEnd());
  if (numeric === null) return null;
  return {
    k: 'literal',
    value: { k: 'number', v: numeric.value / 100 },
    format: numberFormat({
      category: 'percentage',
      decimals: numeric.decimals,
      grouping: numeric.grouping,
    }),
  };
}

/** `$1,234.50`, `-$5`, `€7.25`: a currency symbol prefix over a numeric literal. */
function parseCurrency(text: string): ClassifiedInput | null {
  for (const symbol of CURRENCY_SYMBOLS) {
    for (const [sign, body] of signSplits(text)) {
      if (!body.startsWith(symbol)) continue;
      const numeric = parseNumeric(body.slice(symbol.length).trimStart());
      if (numeric === null) continue;
      // A sign may sit before the symbol (`-$5`) or after it (`$-5`); either way
      // one negation is enough.
      const value = sign === '-' ? -Math.abs(numeric.value) : numeric.value;
      return {
        k: 'literal',
        value: { k: 'number', v: value },
        format: numberFormat({
          category: 'currency',
          decimals: numeric.decimals,
          grouping: numeric.grouping,
          currency: symbol,
        }),
      };
    }
  }
  return null;
}

/** `[sign, rest]` pairs: the text as-is, and with a leading sign split off. */
function signSplits(text: string): [string, string][] {
  const head = text.charAt(0);
  if (head === '-' || head === '+') return [['', text], [head, text.slice(1).trimStart()]];
  return [['', text]];
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const US_DATE_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const ISO_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/;
const TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

function digits(text: string | undefined): number {
  return Number(text ?? '0');
}

/** ISO (`2024-03-05`) and US (`3/5/2024`, `03/05/2024`) dates, with optional time. */
function parseDate(text: string): ClassifiedInput | null {
  const iso = ISO_DATE_RE.exec(text);
  if (iso !== null) {
    return dateLiteral(digits(iso[1]), digits(iso[2]), digits(iso[3]), 'yyyy-mm-dd');
  }

  const us = US_DATE_RE.exec(text);
  if (us !== null) {
    const month = us[1] ?? '';
    const day = us[2] ?? '';
    const padded = month.length === 2 && day.length === 2;
    return dateLiteral(
      digits(us[3]),
      Number(month),
      Number(day),
      padded ? 'mm/dd/yyyy' : 'm/d/yyyy',
    );
  }

  const dt = ISO_DATETIME_RE.exec(text);
  if (dt !== null) {
    const year = digits(dt[1]);
    const month = digits(dt[2]);
    const day = digits(dt[3]);
    const hour = digits(dt[4]);
    const minute = digits(dt[5]);
    const second = dt[6] === undefined ? 0 : digits(dt[6]);
    if (!isValidYmd(year, month, day) || !isValidHms(hour, minute, second)) return null;
    const pattern: DateTimePattern =
      dt[6] === undefined ? 'yyyy-mm-dd hh:mm' : 'yyyy-mm-dd hh:mm:ss';
    return {
      k: 'literal',
      value: { k: 'number', v: serialFromDateTime(year, month, day, hour, minute, second) },
      format: numberFormat({ category: 'date', pattern }),
    };
  }

  return null;
}

function dateLiteral(
  year: number,
  month: number,
  day: number,
  pattern: DateTimePattern,
): ClassifiedInput | null {
  if (!isValidYmd(year, month, day)) return null;
  return {
    k: 'literal',
    value: { k: 'number', v: serialFromDate(year, month, day) },
    format: numberFormat({ category: 'date', pattern }),
  };
}

/** `13:45` and `13:45:30`, stored as a fraction of a day. */
function parseTime(text: string): ClassifiedInput | null {
  const m = TIME_RE.exec(text);
  if (m === null) return null;
  const hour = digits(m[1]);
  const minute = digits(m[2]);
  const second = m[3] === undefined ? 0 : digits(m[3]);
  if (!isValidHms(hour, minute, second)) return null;
  return {
    k: 'literal',
    value: { k: 'number', v: fractionFromTime(hour, minute, second) },
    format: numberFormat({
      category: 'time',
      pattern: m[3] === undefined ? 'hh:mm' : 'hh:mm:ss',
    }),
  };
}

/** A plain number keeps the `general` format unless it was typed with grouping. */
function parsePlainNumber(text: string): ClassifiedInput | null {
  const numeric = parseNumeric(text);
  if (numeric === null) return null;
  const format = numeric.grouping
    ? numberFormat({ category: 'number', decimals: numeric.decimals, grouping: true })
    : numberFormat({ category: 'general' });
  return { k: 'literal', value: { k: 'number', v: numeric.value }, format };
}

/**
 * Classifies typed cell content (Requirements 2.5, 2.6, 2.7).
 *
 * Surrounding whitespace is ignored when matching a literal, so `" 42 "` is the
 * number 42; text keeps the string exactly as typed.
 */
export function classifyInput(text: string): ClassifiedInput {
  if (text.length === 0) return { k: 'blank', value: { k: 'empty' } };
  const trimmedLeft = text.trimStart();
  if (trimmedLeft.startsWith('=')) return { k: 'formula', source: trimmedLeft };

  const trimmed = text.trim();
  if (trimmed.length > 0) {
    const literal =
      parsePercentage(trimmed) ??
      parseCurrency(trimmed) ??
      parseDate(trimmed) ??
      parseTime(trimmed) ??
      parsePlainNumber(trimmed);
    if (literal !== null) return literal;
  }

  return { k: 'text', value: { k: 'text', v: text } };
}

/** The value `classifyInput` would store, ignoring the formula case. */
export function classifiedValue(input: ClassifiedInput): CellValue | null {
  return input.k === 'formula' ? null : input.value;
}
